'use client';

import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { css } from 'styled-system/css';

import { Alert } from '@shaojiang/components/ui/alert';

import { ImageElement, PanoramaMinimapProps } from './types';
import { limitWithinRange } from './utils';

/**
 * This component draws a panorama image on a canvas as a whole.
 * The panorama image is split into multiple images, which are painted on canvas side-by-side.
 * It highlights the visible window on the canvas.
 *
 * @param images An array of image URLs.
 * @param center  The center point of the visible window.
 * @param visiblePanoHeight The Pano height (original image) of the visible window.
 * @param visiblePanoWidth The Pano width (original image) of the visible window.
 * @param panoWidth The Pano width (original image).
 * @param panoHeight The Pano height (original image).
 * @param canvasHeight The height of the canvas.
 */
const PanoramaMinimap = ({
  images,
  center,
  visiblePanoHeight,
  visiblePanoWidth,
  panoWidth,
  panoHeight,
  canvasHeight = 600,
  onMinimapClick,
}: PanoramaMinimapProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageElements, setImageElements] = useState<ImageElement[]>([]);
  const [moving, setMoving] = useState(false);

  const ratio = canvasHeight / panoHeight;
  const canvasWidth = Math.floor(panoWidth * ratio);
  const doi = images.length;
  const singleImageWidth = panoWidth / doi;
  const _debug = process.env.NODE_ENV === 'development';
  const minCenterY = visiblePanoHeight / 2;
  const maxCenterY = panoHeight - minCenterY;

  const handleLoadedImage = useCallback((image: ImageElement) => {
    setImageElements((prevImageElements) => {
      image.ready = true;
      prevImageElements[image.index] = image;
      return [...prevImageElements];
    });
  }, []);

  useEffect(() => {
    // Create an array of image elements. Do not load the images yet, but store the src as `imgSrc`.
    const imgElements = images.map((imgSrc, i) => {
      const img = new Image() as ImageElement;
      img.src = imgSrc;
      img.index = i;
      img.onload = () => handleLoadedImage(img);
      return img;
    });
    setImageElements(imgElements);
  }, [images, handleLoadedImage]);

  const drawWindow = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || imageElements.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const visibleWindowWidth = visiblePanoWidth * ratio;
    const halfVisibleWindowWidth = visibleWindowWidth / 2;
    const visibleWindowHeight = visiblePanoHeight * ratio;
    ctx.fillStyle = 'rgba(225, 225, 225, 0.6)';
    const topHeight = Math.ceil(center.y * ratio - visibleWindowHeight / 2);
    const middleHeight = Math.ceil(visibleWindowHeight);
    const bottomHeight = canvasHeight - topHeight - middleHeight;

    // To highlight the visible window, we split the canvas into 3 or 4 rectangles with gray background:
    //  1. Top: from top to the top of the visible window
    //  2. Middle: left and right of the visible window
    //  3. Bottom: from bottom of the visible window to the bottom of the canvas
    ctx.fillRect(0, 0, canvasWidth, topHeight);
    if (
      center.x * ratio - halfVisibleWindowWidth > 0 &&
      center.x * ratio + halfVisibleWindowWidth < canvasWidth
    ) {
      ctx.fillRect(0, topHeight, center.x * ratio - halfVisibleWindowWidth, middleHeight);
      ctx.fillRect(
        center.x * ratio + halfVisibleWindowWidth,
        topHeight,
        canvasWidth - (center.x * ratio + halfVisibleWindowWidth),
        middleHeight,
      );
    } else {
      // When the visible window is at the edge of the canvas, we need to draw 3 rectangles.
      ctx.fillRect(
        center.x * ratio - halfVisibleWindowWidth < 0
          ? halfVisibleWindowWidth + center.x * ratio
          : halfVisibleWindowWidth - (canvasWidth - center.x * ratio),
        topHeight,
        canvasWidth - visibleWindowWidth,
        middleHeight,
      );
    }
    ctx.fillRect(0, topHeight + middleHeight, canvasWidth, bottomHeight);

    ctx.beginPath();
    ctx.arc(center.x * ratio, center.y * ratio, 2, 0, 2 * Math.PI, false);
    ctx.fillStyle = 'red';
    ctx.fill();
    ctx.lineWidth = 0;
    ctx.strokeStyle = 'transparent';
    ctx.stroke();
  }, [
    visiblePanoWidth,
    visiblePanoHeight,
    imageElements.length,
    center.x,
    center.y,
    ratio,
    canvasHeight,
    canvasWidth,
  ]);

  useEffect(() => {
    drawWindow();
  }, [drawWindow]);

  /**
   * Repaint the canvas.
   */
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || imageElements.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.font = `12px Arial`;
    ctx.fillStyle = '#ccc';

    for (let imgIndex = 0; imgIndex < doi; imgIndex++) {
      const img = imageElements[imgIndex];
      const canvasDrawStartX = imgIndex * singleImageWidth * ratio;

      // drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
      // @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
      img.ready
        ? ctx.drawImage(
            img,
            0,
            0,
            Math.ceil(singleImageWidth),
            canvasHeight / ratio,
            Math.floor(canvasDrawStartX),
            0,
            Math.ceil(singleImageWidth * ratio),
            canvasHeight,
          )
        : ctx.fillText(
            'Loading ...',
            Math.floor(canvasDrawStartX + (singleImageWidth * ratio) / 2 - 25),
            canvasHeight / 2 + 5,
          );
    }
    drawWindow();
  }, [imageElements, ratio, singleImageWidth, doi, canvasHeight, canvasWidth, drawWindow]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  const onMoveToPos = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || imageElements.length === 0) return;

      // If it's 'click' event, move to the clicked position. Otherwise, it's dragging
      if (e.type !== 'click' && !moving) return;

      const rect = canvas.getBoundingClientRect();
      onMinimapClick &&
        onMinimapClick({
          x: (e.clientX - rect.left) / ratio,
          y: limitWithinRange((e.clientY - rect.top) / ratio, minCenterY, maxCenterY),
        });
    },
    [onMinimapClick, imageElements.length, ratio, moving, minCenterY, maxCenterY],
  );

  const onStartToMove = useCallback((e: MouseEvent) => {
    setMoving(true);
  }, []);

  const onStopMoving = useCallback((e: MouseEvent) => {
    setMoving(false);
  }, []);

  return (
    <>
      {_debug && (
        <Alert.Root mb='4' borderColor='info.main'>
          <Alert.Content>
            <Alert.Title>Variables:</Alert.Title>
            <ul className={css({ textStyle: 'sm', listStyle: 'disc', pl: '5' })}>
              <li>centerPoint: {JSON.stringify(center)}</li>
              <li>prop.visiblePanoHeight: {visiblePanoHeight}</li>
              <li>prop.visiblePanoWidth: {visiblePanoWidth}</li>
              <li>ratio: {ratio}</li>
            </ul>
          </Alert.Content>
        </Alert.Root>
      )}
      <div
        onClick={onMoveToPos}
        onMouseMove={onMoveToPos}
        onMouseDown={onStartToMove}
        onMouseUp={onStopMoving}
        onMouseLeave={onStopMoving}
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <canvas ref={canvasRef} height={canvasHeight} width={canvasWidth} inputMode='none' />
      </div>
    </>
  );
};

export default React.memo(PanoramaMinimap);
